import { useRouter } from 'next/navigation';

import { URLSearchParams } from 'url';

import type { AddedAddOn, AddedAddOns } from '@/ducks/addOns/types';

import { toStringArray } from '@/ducks/filters/utils/primitive';
import { AddOnSellType, type TAddOn, type TSortableAddOn } from '@/helpers/api/graphql/queries/AddonAvailability';

import { ADD_ONS_SEARCH_PARAM } from './constants';

export const serializeData = (addOns?: Record<string, number>) => {
  return Object.keys(addOns || {})
    .reduce<string[]>((codes, code) => {
      if (!addOns?.[code]) {
        return codes;
      }
      return [...codes, addOns[code]! > 1 ? `${code}_${addOns[code]}` : code];
    }, [])
    .join(',');
};

export const deserializeData = (searchParams: URLSearchParams, addOns?: TAddOn[], codes?: string[]) => {
  return toStringArray(searchParams.get(ADD_ONS_SEARCH_PARAM)).reduce<AddedAddOns>((map, value) => {
    const index = value.lastIndexOf('_');
    let code = value;
    let amount = 1;
    if (index !== -1) {
      amount = parseInt(value.substring(index + 1));
      if (isNaN(amount)) {
        amount = 1;
      }

      code = value.substring(0, index);
    }

    const addOn = addOns?.find((addOn) => addOn.code === code);

    if ((!addOns || addOn) && (!codes || codes?.includes(code)) && amount > 0) {
      amount = (map[code] || 0) + amount;
      if (addOn && typeof addOn.maximumQuantity === 'number' && amount > addOn.maximumQuantity) {
        amount = addOn.maximumQuantity;
      }

      return { ...map, [code]: amount };
    }

    return map;
  }, {});
};

export const isAdded = (addOns?: AddedAddOns, addOn?: TAddOn) => {
  return addOn && addOns && !!addOns[addOn.code];
};

export const isConfigurableAddOn = (addOn: TAddOn) => addOn.nestedAddOns && addOn.nestedAddOns.length > 0;

export const saveInSearchParams = (router: ReturnType<typeof useRouter>, value?: string) => {
  const url = new URL(window.location.toString());
  const { searchParams } = url;

  if (searchParams.has(ADD_ONS_SEARCH_PARAM)) {
    if (value === searchParams.get(ADD_ONS_SEARCH_PARAM)) {
      return;
    }

    searchParams.delete(ADD_ONS_SEARCH_PARAM);
  }

  if (value) {
    searchParams.append(ADD_ONS_SEARCH_PARAM, value);
  }

  router.replace(url.toString(), { scroll: false });
};

export const getAddOnPrice = (addOn: TAddOn, sailorsAmount: number) => {
  const price = addOn.addOnDetails?.price;

  if (price) {
    let amount = price.amount;
    if (addOn.sellType === AddOnSellType.PerCabin) {
      amount = amount * sailorsAmount;
    }

    return {
      ...price,
      amount,
    };
  }
};

export const getAddOnsTotalPrice = (addOns: TAddOn[], sailorsAmount: number, values?: AddedAddOns) => {
  return {
    ...addOns[0]?.addOnDetails?.price,
    amount: addOns.reduce((totalAmount, addOn) => {
      const price = getAddOnPrice(addOn, sailorsAmount);

      if (!isConfigurableAddOn(addOn)) {
        return totalAmount + (price?.amount || 0) * (values?.[addOn.code] || 0);
      }

      return (
        totalAmount +
        addOn.nestedAddOns!.reduce((amount, addOn) => (price?.amount || 0) * (values?.[addOn.code] || 0) + amount, 0)
      );
    }, 0),
  };
};

export const getAddOnCodes = (addOn: TAddOn) => {
  return addOn.nestedAddOns?.map(({ code }) => code) || [addOn.code];
};

export const isEmptyValues = (addedAddOns: AddedAddOns) => {
  const values = new Set(Object.values(addedAddOns));
  return (values.size === 1 && values.has(0)) || values.size === 0;
};

export const getAddOnsCalcInvoicePayload = (addedAddOns: AddedAddOn[], sailorNumber: number) => {
  return addedAddOns.reduce<{ code: string; quantity: number }[]>((result, addOn) => {
    const item = { code: addOn.code, quantity: addOn.quantity };
    if (
      (addOn.sellType === AddOnSellType.PerSailor && sailorNumber === 1) ||
      addOn.sellType === AddOnSellType.PerCabin
    ) {
      return [...result, item];
    }

    return result;
  }, []);
};

export const sortByPriority = <T extends TSortableAddOn>(a: T, b: T) =>
  (a.bookingSortPriority || -1) - (b.bookingSortPriority || -1);
